import { Box, Icon, Textarea } from "@nulogy/components";
import Field from "components/common/Field";
import FieldRow from "components/common/FieldRow";
import Section from "components/common/Section";
import {
  formatDateTime,
  formatDurationInHours,
  formatEfficiency,
  formatLabor,
  formatPerformance,
  formatQuantity,
  formatProductionRate as rawFormatProductionRate,
} from "components/helpers/format";
import PackmanagerJobLink from "components/pages/schedule/PackmanagerJobLink";
import PackmanagerWorkOrderLink from "components/pages/schedule/PackmanagerWorkOrderLink";
import BlockModal from "components/pages/schedule/styles/BlockModal";
import ProductionDetailsSummary from "components/pages/schedule/workBlocks/ProductionDetailsSummary";
import WorkBlock from "domain/WorkBlock";
import useApplicationContext from "hooks/apollo/applicationContext/useApplicationContext";
import React from "react";
import DateTime from "utils/DateTime";

const TextArea = ({ value }) => (
  <Box marginBottom="x2">
    <Textarea disabled rows={5} value={value || ""} />
  </Box>
);

export default function ReadOnlyWorkBlockModalView({
  isOpen,
  onClose,
  blockData,
  effectiveProductionRate,
  workBlockQuantity,
  productionRateLabel,
  workBlockLayout,
}) {
  const {
    block,
    block: {
      workOrder: { notes, unitOfMeasure, code, externalUuid },
      line: { name: lineName },
      jobId,
      standardProductionRate,
      standardPeople,
      productionRateOverride,
      performance,
      performanceOverride,
      efficiencyOverride,
      laborOverride,
      lineEfficiency,
      teardownTimeLabel,
      teardownTimeDuration,
      workTimeStartsAt,
      workTimeEndsAt,
      lockedOnSchedule,
    },
  } = blockData;

  const applicationContext = useApplicationContext();
  const description = WorkBlock.description(block, workBlockLayout);
  const formatProductionRate = (productionRate) => rawFormatProductionRate({ productionRate, unitOfMeasure });

  return (
    <BlockModal className="spec-block-modal" isOpen={isOpen} onRequestClose={onClose} title="Work block">
      <Section
        controls={
          <Box marginBottom="x2">
            {lockedOnSchedule && <Icon color="darkBlue" icon="lock" mr="x1" pt="4px" size="28px" />}
          </Box>
        }
        title="Scheduling details"
      >
        <Field className="spec-selected-work-order" labelText="Work order">
          {description && `${description} • `}
          <PackmanagerWorkOrderLink code={code} externalUuid={externalUuid} />
        </Field>
        {jobId && (
          <FieldRow data-testid="published-job">
            <Field labelText="Job">
              <PackmanagerJobLink applicationContext={applicationContext} jobId={jobId} />
            </Field>
          </FieldRow>
        )}
        <Field labelText="Line">{lineName}</Field>
        <FieldRow>
          <Field labelText="Work starts">{formatDateTime(workTimeStartsAt)}</Field>
          <Field labelText="Work ends">{formatDateTime(workTimeEndsAt)}</Field>
        </FieldRow>
      </Section>
      <Section className="spec-teardown-time-section" title="Teardown time">
        <FieldRow>
          <Field labelText="Label">{teardownTimeLabel}</Field>
          <Field labelText="Duration (h)">{formatDurationInHours(teardownTimeDuration)}</Field>
        </FieldRow>
        {teardownTimeDuration > 0 && (
          <FieldRow>
            <Field labelText="Teardown starts">{formatDateTime(workTimeEndsAt)}</Field>
            <Field labelText="Teardown ends">
              {formatDateTime(DateTime.addDuration(workTimeEndsAt, teardownTimeDuration, "seconds"))}
            </Field>
          </FieldRow>
        )}
      </Section>
      <Section title="Production details">
        <FieldRow>
          <Field labelText={productionRateLabel}>{formatProductionRate(standardProductionRate)}</Field>
          <Field labelText="Override">{formatProductionRate(productionRateOverride)}</Field>
        </FieldRow>
        <FieldRow>
          <Field labelText="Performance">{formatPerformance(performance)}</Field>
          <Field labelText="Override">{formatPerformance(performanceOverride)}</Field>
        </FieldRow>
        <FieldRow>
          <Field labelText="Standard people">{formatLabor(standardPeople)}</Field>
          <Field labelText="Override">{formatQuantity(laborOverride)}</Field>
        </FieldRow>
        <FieldRow>
          <Field labelText="Line efficiency">{formatEfficiency(lineEfficiency, { suffix: false })}</Field>
          <Field labelText="Override">{formatEfficiency(efficiencyOverride)}</Field>
        </FieldRow>
        <ProductionDetailsSummary
          effectiveProductionRate={formatQuantity(effectiveProductionRate)}
          expectedQuantity={formatQuantity(workBlockQuantity)}
          unitOfMeasure={unitOfMeasure}
        />
      </Section>
      <TextArea value={notes} />
    </BlockModal>
  );
}
